import { NormalizedFormState, normalizeSection } from '../../config.models';
import { SensorTypes } from '../common.models';
import { FlexInfoFieldId, FlexSectionIds } from './base-config-models';
import { FlexLocationCardState } from './models';
import { mp2basicInfo } from './mp2-basic-info';
import { RegisterEntityType } from '../../models/dynamic-section-models';

const MP2_TITLE_PREFIX = 'MP2: ';

export interface FlexEntityTypeFieldState {
    value: RegisterEntityType | null;
    preference?: RegisterEntityType;
    visible: boolean;
    readonly: boolean;
    disabled: boolean;
}

export function getFlexEntityTypeFieldState(
    sources: readonly RegisterEntityType[] | undefined,
    currentValue?: RegisterEntityType | null,
    preserveCurrentValue: boolean = false,
): FlexEntityTypeFieldState {
    if (!sources?.length) {
        return {
            value: null,
            visible: false,
            readonly: false,
            disabled: true,
        };
    }

    const hasValidCurrentValue =
        preserveCurrentValue &&
        currentValue !== undefined &&
        currentValue !== null &&
        (currentValue === RegisterEntityType.Auto || sources.includes(currentValue));
    const value = hasValidCurrentValue ? currentValue : RegisterEntityType.Auto;

    return {
        value,
        preference: value,
        visible: true,
        readonly: false,
        disabled: false,
    };
}

export function flexMonitorPointChange(formState: NormalizedFormState, index: 0 | 1) {
    const flexInfo = formState.config.sections.find((section) => section.id === FlexSectionIds.FlexInfo);
    const mp1 = flexInfo.fields[FlexInfoFieldId.MP1];
    const mp2 = flexInfo.fields[FlexInfoFieldId.MP2];

    // we do not allow user to pick mp2 when only one or none sensors selected
    if (index === 0 && mp1.value === 1 && mp2.value === null) {
        mp1.value = 0;

        return;
    }

    const newMpValue = index === 0 ? mp1.value : mp2.value;
    const sensorSection = formState.config.sections.find(
        (section) => section.metadata?.type === SensorTypes.flex && section.metadata.sensorIndex === index,
    );

    if (sensorSection && sensorSection.metadata.mp !== newMpValue) {
        sensorSection.metadata.mp = newMpValue;

        let newId = '';
        if (newMpValue === 0) {
            newId = sensorSection.id.replace(/mp2/, 'mp1');
            sensorSection.title = sensorSection.title.replace(/MP2: /, '');
        } else {
            newId = sensorSection.id.replace(/mp1/, 'mp2');
            sensorSection.title = MP2_TITLE_PREFIX + sensorSection.title;
        }

        Object.keys(sensorSection.fields).forEach((key) => {
            sensorSection.fields[key].path = sensorSection.fields[key].path.replace(sensorSection.id, newId);
        });

        sensorSection.id = newId;
    }
}

export function flexCheckMp2(state: FlexLocationCardState) {
    const shouldHaveMp2 = state.formState.config.sections.find(
        (section) => section.metadata && section.metadata.type === SensorTypes.flex && section.metadata.mp === 1,
    );
    const mp2InfoExist = state.formState.config.sections.find((section) => section.id === FlexSectionIds.Mp2BasicInfo);

    if (shouldHaveMp2 && !mp2InfoExist) {
        const normalizedMp2 = normalizeSection(mp2basicInfo(state));

        state.formState.config.sections.push(normalizedMp2);
    } else if (!shouldHaveMp2 && mp2InfoExist) {
        state.formState.config.sections = state.formState.config.sections.filter(
            (section) => section.id !== FlexSectionIds.Mp2BasicInfo,
        );
    }
}

export function sortFlexSections(formState: NormalizedFormState) {
    const basicInfo = formState.config.sections.find((section) => section.id === FlexSectionIds.BasicInfo);
    const flexInfo = formState.config.sections.find((section) => section.id === FlexSectionIds.FlexInfo);
    const dataDelivery = formState.config.sections.find((section) => section.id === FlexSectionIds.DataDelivery);
    const configureAlarms = formState.config.sections.find(
        (section) => section.id === FlexSectionIds.ConfigureAlarms,
    );
    const sensor1 = formState.config.sections.find(
        (section) =>
            section.metadata && section.metadata.type === SensorTypes.flex && section.metadata.sensorIndex === 0,
    );
    const mp2BasicInfo = formState.config.sections.find((section) => section.id === FlexSectionIds.Mp2BasicInfo);
    const sensor2 = formState.config.sections.find(
        (section) =>
            section.metadata && section.metadata.type === SensorTypes.flex && section.metadata.sensorIndex === 1,
    );

    const dialogActions = formState.config.sections.find((section) => section.id === FlexSectionIds.DialogActions);
    const mp1Field = flexInfo.fields[FlexInfoFieldId.MP1];
    const mp2Field = flexInfo.fields[FlexInfoFieldId.MP2];

    formState.config.sections = [
        basicInfo,
        flexInfo,
        mp1Field.value === 0 ? sensor1 : null,
        mp2Field.value === 0 ? sensor2 : null,
        configureAlarms,
        dataDelivery,
        mp2BasicInfo,
        mp1Field.value === 1 ? sensor1 : null,
        mp2Field.value === 1 ? sensor2 : null,
        dialogActions,
    ].filter((v) => !!v);
}
